import { useParams } from 'react-router-dom';
import { InfoCircleTwoTone } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Popconfirm,
  Popover,
  Row,
  Space,
} from 'antd';

import { ISnapStore, snapStore } from 'containers/Snaps/Snap/store';
import { MilesText } from 'components';

import { usePatchRequest } from 'hooks';

import { SnapDetails } from 'codegen';

import {
  confidenceColors,
  handleEditOCR,
  handlePatchAllOCR,
  handlePatchOCR,
  handleSuccessPatchOCR,
} from './lib';

const { Item, useFormInstance } = Form;

interface IOCRActionsProps {
  isEditDisabled: boolean;
  set: (value: boolean) => void;
}

export const OCRActions = ({ isEditDisabled, set }: IOCRActionsProps) => {
  const form = useFormInstance();
  const ocrScores = snapStore((state) => state.ocrScores);
  const ocrFormValues = snapStore((state) => state.ocrFormValues);

  const { snapId } = useParams();

  const [patchSnapsDetails, { loading }] = usePatchRequest<SnapDetails, ISnapStore['ocrScores'] | ISnapStore['ocrFormValues']>({
    url: `/snaps/details/${snapId}`,
    options: {
      onSuccess: handleSuccessPatchOCR(set),
    },
  });

  return (
    <Item
      noStyle
      shouldUpdate
    >
      {() => (
        <Space.Compact>
          <Popover
            title='Confidence score'
            placement='bottomRight'
            content={(
              <Row className='w-[240px]'>
                <Col xs={24}>
                  <Space direction='vertical' size={10} className='w-full'>
                    {confidenceColors.map(({ title, color }) => (
                      <Row key={title} justify='space-between'>
                        <MilesText>{title}</MilesText>

                        <div
                          style={{ backgroundColor: color }}
                          className='h-[20px] w-[80px] border-1  border-solid border-[#D9D9D9]'
                        />
                      </Row>
                    ))}
                  </Space>
                </Col>
              </Row>
            )}
          >
            <InfoCircleTwoTone className='text-[18px] pr-5 flex items-center' />
          </Popover>

          <Popconfirm
            title={(
              <div className='w-[200px]'>
                Will approve only Receipt Date, Subtotal, Total Tax, Total fields.
              </div>
            )}
            okText='Yes'
            cancelText='No'
            placement='bottom'
            onConfirm={handlePatchAllOCR(ocrScores, patchSnapsDetails)}
            // bcoz of disabled parent form
            okButtonProps={{ disabled: false }}
            cancelButtonProps={{ disabled: false }}
          >
            <Button disabled={loading}>Looks Good</Button>
          </Popconfirm>

          <Button
            disabled={loading}
            onClick={handleEditOCR(form, isEditDisabled, set)}
          >
            {isEditDisabled ? 'Edit' : 'Undo'}
          </Button>

          <Popconfirm
            title={(
              <div className='w-[200px]'>
                Do you want to apply made changes?
                Note that they may result in receipt reprocessing.
              </div>
            )}
            okText='Yes'
            cancelText='No'
            placement='bottom'
            onConfirm={handlePatchOCR(form, ocrFormValues, patchSnapsDetails)}
            disabled={
              !form?.isFieldsTouched()
              || isEditDisabled
              || !!form.getFieldsError().some(({ errors }) => !!errors.length)
            }
          >
            <Button
              type='primary'
              loading={loading}
              disabled={
                !form?.isFieldsTouched()
                || isEditDisabled
                || !!form.getFieldsError().some(({ errors }) => !!errors.length)
              }
            >
              Save
            </Button>
          </Popconfirm>
        </Space.Compact>
      )}
    </Item>
  );
};
